import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import * as moment from "moment";
import { Subscription } from 'rxjs';
import { EventLoggingParams, LoginType } from "src/app/interfaces/log";
import { SurveyComment, SurveyDetail } from 'src/app/interfaces/survey';
import { LogService } from 'src/app/services/log/log.service';
import { ModalService } from "src/app/services/modal/modal.service";
import { RouterService } from "src/app/services/router/router.service";
import { UserService } from 'src/app/services/user/user.service';
import { MbtiCreateAlertComponent } from './../../../common/modals/mbti-create-alert/mbti-create-alert.component';
import { MBTI } from './../../../interfaces/mbti';
import { SurveyOption } from './../../../interfaces/survey';
import { MbtiService } from './../../../services/mbti/mbti.service';
import { SurveyCommentSubjectParams, SurveyService } from './../../../services/survey/survey.service';

@Component({
  selector: 'survey-main',
  templateUrl: './survey-main.component.html',
  styleUrls: ['./survey-main.component.scss']
})
export class SurveyMainComponent implements OnInit, OnDestroy {
  constructor(
    private modalService: ModalService,
    private surveyService: SurveyService,
    private mbtiService: MbtiService,
    private logService: LogService,
    private userService: UserService,
    private routerService: RouterService
  ) { }

  private baseTitle = '{mbti}는 어떻게 생각해요?';
  // home화면의 것인지 result modal의 것인지..
  @Input() type: 'home' | 'result-modal' = 'home';
  @Input() title = this.baseTitle;
  @Input() survey: SurveyDetail | undefined;
  @Output() optionChanged = new EventEmitter<SurveyOption>();
  month: number | undefined;
  date: number | undefined;
  showLoading = false;
  private isResultModalOpened = false;
  private surveyChangedSubscription: Subscription | undefined;
  private surveyCommentSubscription: Subscription | undefined;
  private userSubscription: Subscription | undefined;

  get exp012(): boolean {
    return this.logService.exp.exp012;
  }

  get myMbti(): MBTI | null | undefined {
    return this.mbtiService.myMbti;
  }

  ngOnInit() {
    if (this.survey !== undefined) {
      this.setSurveyDate(this.survey);
    } else {
      this.getTodaySurvey();
    }
    this.setMyMbti();
    if (this.userSubscription === undefined) {
      this.userSubscription = this.userService.getUserSubject().subscribe(() => {
        this.setMyMbti();
      })
    }
    if (this.surveyChangedSubscription === undefined) {
      this.surveyChangedSubscription = this.surveyService.getSurveyChangedSubject().subscribe(() => {
        this.getTodaySurvey(() => {
          if (this.isResultModalOpened || this.type !== 'home') {
            return;
          }
          const modalTimeout = setTimeout(() => {
            this.onClickSurveyResult();
            clearTimeout(modalTimeout);
          }, 300);
        });

      })
    }

    if (this.surveyCommentSubscription === undefined) {
      this.surveyCommentSubscription = this.surveyService.getSurveyCommentSubject().subscribe((data: SurveyCommentSubjectParams) => {
        if (data.type === 'delete') {
          this.onDeleteComment(data.comment.id);
        }
        if (data.type === 'update') {
          this.onUpdateComment(data.comment);
        }
        if (data.type === 'like') {
          this.onLikeComment(data.comment);
        }
      })
    }
  }

  ngOnDestroy() {
    if (this.surveyChangedSubscription !== undefined) {
      this.surveyChangedSubscription.unsubscribe();
    }
  }

  doRefresh(event): void {
    this.ngOnDestroy();
    this.ngOnInit();
  }

  async onClickSurveyResult(action?: 'focusComment' | 'scrollComment'): Promise<void> {
    const loggingParams: EventLoggingParams = {
      screenName: 'home', //화면 식별자 
      surveyParticipants: this.survey?.totalAnsweredCount,// 참여자 숫자
      userMbti: this.myMbti,//접속자의 MBTI
      surveyId: this.survey?.id,// 서베이의 주제 식별자
      thisSurveyAnswered: this.survey?.isAnswered,// 해당 서베이 응답 여부
    }
    this.logService.survey.homeSurveyResultClick(loggingParams);
    if (this.userService.navigateToLoginIfAnonymous(LoginType.CLICK_SHOW_SURVEY_RESULT)) {
      return;
    }

    this.logService.survey.surveyResultClickResultButton()

    if (this.isResultModalOpened || this.survey === undefined || this.myMbti === undefined || this.type !== 'home') {
      return;
    }
    this.routerService.navigate(`survey/${this.survey.id}`)
  }

  async onClickSurveyItem(option: SurveyOption): Promise<void> {
    if (this.survey === undefined || this.showLoading) {
      return;
    }

    const loggingParams: EventLoggingParams = {
      screenName: 'home',//화면 식별자
      surveyParticipants: this.survey.totalAnsweredCount,// 참여자 숫자
      surveyId: this.survey.id,// 서베이의 주제 식별자
    }
    this.logService.survey.homeSurveyAnswerClicked(loggingParams);

    if (!this.myMbti) {
      const alertModal = await this.modalService.openBottomSheet(MbtiCreateAlertComponent)
      await alertModal.present();
      return;
    }

    // 이미 선택한 옵션일 경우
    if (this.survey.options
      .filter(option => option.isChosen)
      .map((option) => option.id)
      .includes(option.id)) {
      return;
    }

    if (this.userService.navigateToLoginIfAnonymous(LoginType.CLICK_SURVEY_ITEM)) {
      return;
    }

    // 응답 로깅
    if (!this.survey.isAnswered) {
      if (this.type === 'home') {
        this.logService.survey.homeSurveyAnswerPosted(loggingParams);
      } else {
        this.logService.survey.surveyResultSurveyAnswerPosted(loggingParams)
      }
    }

    // 응답 수정 로깅
    if (this.survey.isAnswered) {
      if (this.type === 'home') {
        this.logService.survey.homeSurveyAnswerUpdated(loggingParams);
      } else {
        this.logService.survey.surveyResultSurveyAnswerUpdated(loggingParams);
      }
    }


    this.showLoading = true;
    this.surveyService.postSurveyAnswer(this.survey.id, option.id).subscribe(() => {
      this.surveyService.surveyChanged();
      this.survey?.options.forEach((item) => {
        item.isChosen = item.id === option.id;
        if (item.isChosen) {
          this.optionChanged.emit(item);
        }

        if (this.survey !== undefined) {
          this.survey.isAnswered = true;
        }
      })

    }, error => {
      console.error(error);
      this.showLoading = false;
    });
    // 만약 Loading 안사라질 경우 대비.
    const loadingTimeout = setTimeout(() => {
      this.showLoading = false;
      clearTimeout(loadingTimeout);
    }, 2000);
  }

  /** main component에서 활용. */
  homeSurveyImpressionLogging(): void {
    this.logService.survey.homeSurveyImpression({
      screenName: 'home',
      surveyParticipants: this.survey?.totalAnsweredCount,
      surveyId: this.survey?.id
    });
  }

  onClickShowAll(): void {
    this.onClickSurveyResult('scrollComment');
    this.logService.survey.homeSurveyShowAllComments();
  }

  async onClickComment(): Promise<void> {
    this.logService.survey.homeSurveyCommentWriteClicked();
    if (!this.myMbti) {
      const alertModal = await this.modalService.openBottomSheet(MbtiCreateAlertComponent)
      await alertModal.present();
      return;
    }
    this.onClickSurveyResult('focusComment');
  }

  private getTodaySurvey(callback?: () => void): void {
    this.surveyService.getTodaySurvey().subscribe((res) => {
      this.survey = res.data;
      this.showLoading = false;
      this.setSurveyDate(res.data);
      this.logService.survey.setSurveyCommonParams({
        surveyId: this.survey?.id,
        surveyParticipants: this.survey?.totalAnsweredCount,
        commentCount: this.survey?.commentCount,
        bestCommentAuthorMbti: this.survey?.bestComment?.writerMbtiType,
        bestCommentId: this.survey?.bestComment?.id,
        bestCommentLength: this.survey?.bestComment?.content?.length,
        bestCommentLikeCount: this.survey?.bestComment?.likeCount
      })
      if (callback !== undefined) {
        callback();
      }
    })
  }

  private setSurveyDate(survey: SurveyDetail): void {
    const date = moment(survey.date);
    this.month = date.month() + 1;
    this.date = date.date();
  }

  private setMyMbti(): void {
    if (!this.userService.isLoggedIn) {
      this.setTitle();
      return;
    }
    if (this.mbtiService.myMbti === undefined) {
      this.setTitle()
      this.mbtiService.getMyMbtiHostQuizReportList(null).subscribe((res) => {
        if (Array.isArray(res.data)) {
          const [firstData] = res.data;
          this.mbtiService.myMbti = firstData?.mbtiType ?? null;
          this.mbtiService.myMbti && this.setTitle(this.mbtiService.myMbti);
        }
      });
    } else {
      this.mbtiService.myMbti && this.setTitle(this.mbtiService.myMbti);
    }
  }

  private setTitle(mbti?: MBTI): void {
    if (mbti === undefined) {
      this.title = '성격 유형별로 답하는 설문조사';
      return;
    }
    if (this.baseTitle.includes('{mbti}')) {
      this.title = this.baseTitle.replace('{mbti}', mbti);
    }
  }

  private onDeleteComment(id: number): void {
    if (this.survey?.bestComment && this.survey.bestComment.id) {
      if (this.survey.bestComment.id === id) {
        this.getTodaySurvey();
      }
    }
  }

  private onUpdateComment(data: Partial<SurveyComment>): void {
    if (this.survey?.bestComment && this.survey.bestComment.id) {
      if (this.survey.bestComment.id === data.id) {
        this.survey.bestComment = {
          ...this.survey.bestComment,
          ...data
        }
      }
    }
  }

  private onLikeComment(data: Partial<SurveyComment>): void {
    if (data.isLikeClicked && !this.survey?.bestComment) {
      this.getTodaySurvey();
    }
  }
}